import type { CellContext } from '@tanstack/react-table';
import * as React from 'react';
import type { DataGridColumnDef, DataGridColumnDefOptions } from '../data-grid.type';
import {
	Tooltip,
	TooltipProvider,
	TooltipTrigger,
	TooltipContent,
	CheckCircle,
	UnCheckCircle,
} from '@components/atoms';

/**
 * Get Simple Type Column Definition
 * @param col {DataGridColumnDef<T>} Column Definition
 * @returns {function} Simple Type Cell
 */
export function getColumnDefSimpleType<T>(col: DataGridColumnDef<T>, options: DataGridColumnDefOptions) {
	return function SimpleTypeCell(context: CellContext<T, unknown>) {
		let value = context.getValue();
		const row = context.row.original;

		if (options?.parse) {
			value = options.parse(value, row, options);
		}

		if (value == null) {
			return '';
		}

		switch (col.type) {
			case 'boolean': {
				return value ? <CheckCircle className="text-success" /> : <UnCheckCircle className="text-disabled" />;
			}

			case 'date': {
				try {
					const date = new Date(value as string | number | Date);
					return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
				} catch {
					return '';
				}
			}

			case 'number': {
				const num = Number(value);

				return isNaN(num) ? '' : num.toString();
			}

			case 'status': {
				return String(value);
			}

			default: {
				// return String(value);
			}
		}

		const cellValue = value instanceof Array ? (value as string[]).join(', ') : String(value);

		return <SimpleWithTooltipCell col={col} context={context} value={cellValue} />;
	};
}

function SimpleWithTooltipCell<T>({
	col,
	context,
	value,
}: {
	col: DataGridColumnDef<T>;
	context: CellContext<T, unknown>;
	value: string;
}) {
	const shouldShowTooltip = col.type === 'text';

	const [isOverflowing, setIsOverflowing] = React.useState(false);
	const cellRef = React.useRef<HTMLSpanElement>(null);

	React.useEffect(() => {
		if (cellRef.current) {
			setIsOverflowing(cellRef.current.scrollWidth > cellRef.current.clientWidth);
		}
	}, [value]);

	const cellContent = (
		<span ref={cellRef} className="block max-w-full overflow-hidden truncate text-ellipsis whitespace-nowrap">
			{value}
		</span>
	);

	return (
		<TooltipProvider delayDuration={0} skipDelayDuration={0}>
			<div data-aid="data-grid-simple-cell-tooltip" className="relative">
				{shouldShowTooltip && isOverflowing ? (
					<Tooltip>
						<TooltipTrigger asChild>{cellContent}</TooltipTrigger>
						<TooltipContent>{value}</TooltipContent>
					</Tooltip>
				) : (
					cellContent
				)}
			</div>
		</TooltipProvider>
	);
}
